import { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import { Button, Steps, Spin, Checkbox } from 'antd';
import {
  RightOutlined,
  LeftOutlined,
  CloseOutlined,
  MailOutlined,
  FileTextOutlined,
  LaptopOutlined,
} from '@ant-design/icons';
import {
  WalkthroughStep,
  WalkthroughState,
} from '../../interfaces/walkthrough';
import { WalkthroughService } from '../../services/walkthroughService';
import { sendSetupLink } from '../../services/account.service';
import { trackPageView, trackEvent } from '../../utils/monitoring';
import {
  initClarity,
  stopClarity,
  claritySetTag,
  clarityEvent,
  clarityIdentify,
} from '../../utils/clarity';
import { useAuth0 } from '@auth0/auth0-react';
import { ReactComponent as Logo } from '../../assets/logo.svg';
import './Walkthrough.less';

interface WalkthroughProps {
  steps: WalkthroughStep[];
  onComplete?: (dontShowAgain: boolean) => void;
  onSkip?: () => void;
  visible?: boolean;
  navigationDisabled?: boolean;
  isStepComplete?: (stepId: string) => boolean;
}

export interface WalkthroughRef {
  advanceStep: () => void;
}

const Walkthrough = forwardRef<WalkthroughRef, WalkthroughProps>(
  (
    {
      steps,
      onComplete,
      onSkip,
      visible = true,
      navigationDisabled = false,
      isStepComplete,
    },
    ref
  ) => {
    const { user } = useAuth0();
    const [state, setState] = useState<WalkthroughState>({
      isVisible: false,
      currentStepIndex: 0,
      isCompleted: false,
      hasSkipped: false,
      dontShowAgain: false,
    });

    const [isLoading, setIsLoading] = useState(true);
    const [mobileNoticeDismissed, setMobileNoticeDismissed] = useState(false);
    const [sendingSetupLink, setSendingSetupLink] = useState(false);
    const [setupLinkSent, setSetupLinkSent] = useState(false);
    const [setupLinkError, setSetupLinkError] = useState(false);
    const isMobile = useIsMobile();

    const currentStep = steps[state.currentStepIndex];
    const isLastStep = state.currentStepIndex === steps.length - 1;
    const isFirstStep = state.currentStepIndex === 0;
    const isCurrentStepComplete = isStepComplete
      ? isStepComplete(currentStep.id)
      : true;
    const isSkipAvailable = !isLastStep;

    // Track page view for each walkthrough step when visible
    // Use currentStep.id in deps (not the object) to avoid re-tracking on parent re-renders
    useEffect(() => {
      if (state.isVisible && currentStep) {
        trackPageView(
          `Walkthrough - ${currentStep.title}`,
          `/walkthrough/${currentStep.id}`,
          {
            walkthrough_step: currentStep.id,
            walkthrough_step_index: state.currentStepIndex + 1,
            walkthrough_total_steps: steps.length,
          }
        );
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.isVisible, state.currentStepIndex, currentStep?.id]);

    // Lock body scroll and hide HubSpot chat when overlay is visible
    useEffect(() => {
      if (state.isVisible) {
        document.body.style.overflow = 'hidden';
        document.body.classList.add('walkthrough-active');
      } else {
        document.body.style.overflow = '';
        document.body.classList.remove('walkthrough-active');
      }
      return () => {
        document.body.style.overflow = '';
        document.body.classList.remove('walkthrough-active');
      };
    }, [state.isVisible]);

    // Initialize Clarity for walkthrough session tracking
    // This enables session replay only during the onboarding walkthrough
    // to understand where users are dropping off, while minimizing privacy impact
    useEffect(() => {
      if (state.isVisible) {
        const initialized = initClarity();
        if (initialized) {
          // Tag this session as a walkthrough session for filtering in Clarity dashboard
          claritySetTag('session_type', 'walkthrough');
        }
      }

      // Always stop Clarity on cleanup (unmount or when isVisible changes to false)
      // This ensures Clarity stops even if user navigates away without completing/skipping
      // The stopClarity function is idempotent - safe to call even if not initialized
      return () => {
        stopClarity();
      };
    }, [state.isVisible]);

    // Identify user in Clarity when available (separate effect to avoid restarting Clarity session)
    useEffect(() => {
      if (state.isVisible && user?.sub) {
        clarityIdentify(user.sub);
      }
    }, [state.isVisible, user?.sub]);

    useEffect(() => {
      const updateVisibility = async () => {
        setIsLoading(true);

        try {
          if (visible) {
            const shouldShow = await WalkthroughService.shouldShowWalkthrough();
            if (shouldShow) {
              const savedStep = await WalkthroughService.getSavedStepIndex();
              const validStep = Math.min(savedStep, steps.length - 1);
              setState((prev) => ({
                ...prev,
                isVisible: true,
                currentStepIndex: validStep,
              }));
            } else {
              setState((prev) => ({
                ...prev,
                isVisible: false,
              }));
            }
          } else {
            setState((prev) => ({
              ...prev,
              isVisible: false,
            }));
          }
        } catch (error) {
          console.error('Failed to determine walkthrough visibility:', error);
          setState((prev) => ({
            ...prev,
            isVisible: visible,
            currentStepIndex: 0,
          }));
        } finally {
          setIsLoading(false);
        }
      };

      updateVisibility();
    }, [visible, steps.length]);

    // Listen for restart walkthrough events
    useEffect(() => {
      const handleRestartWalkthrough = async () => {
        setMobileNoticeDismissed(false);
        setSetupLinkSent(false);
        setSendingSetupLink(false);
        setSetupLinkError(false);
        setIsLoading(true);

        try {
          if (visible) {
            const shouldShow = await WalkthroughService.shouldShowWalkthrough();
            setState((prev) => ({
              ...prev,
              isVisible: shouldShow,
              currentStepIndex: 0,
              isCompleted: false,
              hasSkipped: false,
            }));
          } else {
            setState((prev) => ({
              ...prev,
              isVisible: false,
              currentStepIndex: 0,
              isCompleted: false,
              hasSkipped: false,
            }));
          }
        } catch (error) {
          console.error('Failed to restart walkthrough:', error);
          setState((prev) => ({
            ...prev,
            isVisible: visible,
            currentStepIndex: 0,
            isCompleted: false,
            hasSkipped: false,
          }));
        } finally {
          setIsLoading(false);
        }
      };

      window.addEventListener('restart-walkthrough', handleRestartWalkthrough);
      return () => {
        window.removeEventListener(
          'restart-walkthrough',
          handleRestartWalkthrough
        );
      };
    }, [visible]);

    const handleNext = () => {
      if (isLastStep) {
        handleComplete();
      } else {
        const newIndex = state.currentStepIndex + 1;
        setState((prev) => ({
          ...prev,
          currentStepIndex: newIndex,
        }));
        WalkthroughService.saveProgress(newIndex).catch((error) => {
          console.error('Failed to save walkthrough progress:', error);
        });
      }
    };

    const handlePrevious = () => {
      if (!isFirstStep) {
        const newIndex = state.currentStepIndex - 1;
        setState((prev) => ({
          ...prev,
          currentStepIndex: newIndex,
        }));
        WalkthroughService.saveProgress(newIndex).catch((error) => {
          console.error('Failed to save walkthrough progress:', error);
        });
      }
    };

    const handleComplete = () => {
      trackEvent('walkthrough_complete', {
        step_id: currentStep.id,
        step_index: state.currentStepIndex,
      });
      clarityEvent('walkthrough_complete');
      WalkthroughService.markAsCompleted(state.dontShowAgain).catch((error) => {
        console.error('Failed to mark walkthrough as completed:', error);
      });
      setState((prev) => ({
        ...prev,
        isVisible: false,
        isCompleted: true,
      }));
      stopClarity(); // Stop session recording after walkthrough
      onComplete?.(state.dontShowAgain);
    };

    const handleSkip = () => {
      trackEvent('walkthrough_skip', {
        step_id: currentStep.id,
        step_index: state.currentStepIndex,
      });
      clarityEvent(`walkthrough_skip_at_${currentStep.id}`);
      WalkthroughService.markAsCompleted(state.dontShowAgain).catch((error) => {
        console.error('Failed to mark walkthrough as skipped:', error);
      });
      setState((prev) => ({
        ...prev,
        isVisible: false,
        hasSkipped: true,
      }));
      stopClarity(); // Stop session recording after walkthrough
      onSkip?.();
    };

    // Expose methods to parent components via ref
    useImperativeHandle(ref, () => ({
      advanceStep: handleNext,
    }));

    if (isLoading && visible) {
      return (
        <div className="walkthrough-overlay" data-testid="walkthrough-overlay">
          <div className="walkthrough-loading">
            <Spin size="large" />
            <div className="walkthrough-loading-text">
              Preparing your setup...
            </div>
          </div>
        </div>
      );
    }

    if (!state.isVisible) {
      return null;
    }

    if (isMobile && !mobileNoticeDismissed) {
      return (
        <div
          className="walkthrough-overlay walkthrough-mobile-notice"
          data-testid="walkthrough-mobile-notice"
        >
          <div className="walkthrough-mobile-notice-content">
            <div className="walkthrough-logo">
              <Logo />
            </div>
            <h2 className="walkthrough-mobile-notice-title">
              Welcome to Cirra AI
            </h2>
            <p className="walkthrough-mobile-notice-body">
              We&apos;re excited to help you streamline your Salesforce
              administration with AI.
            </p>
            <p className="walkthrough-mobile-notice-body">
              However, this screen is not the ideal size. The setup walkthrough
              guides you through connecting your AI client (Claude, ChatGPT, VS
              Code, etc.), which works better on a desktop or tablet.
            </p>
            <LaptopOutlined className="walkthrough-mobile-notice-icon" />
            <Button
              type="primary"
              size="large"
              className="walkthrough-mobile-notice-cta"
              loading={sendingSetupLink}
              disabled={setupLinkSent}
              onClick={async () => {
                setSendingSetupLink(true);
                setSetupLinkError(false);
                try {
                  await sendSetupLink();
                  setSetupLinkSent(true);
                } catch {
                  setSetupLinkError(true);
                } finally {
                  setSendingSetupLink(false);
                }
              }}
            >
              {setupLinkSent ? 'Email sent!' : 'Email me a setup link'}
            </Button>
            {setupLinkError && (
              <p className="walkthrough-mobile-notice-error">
                Could not send email. Please try again later.
              </p>
            )}
            <Button
              type="link"
              size="small"
              onClick={() => setMobileNoticeDismissed(true)}
            >
              Continue setup anyway
            </Button>
            <Button type="link" size="small" onClick={handleSkip}>
              Skip setup
            </Button>
            <div className="walkthrough-mobile-notice-support">
              <a
                href="mailto:support@cirra.ai"
                className="walkthrough-support-link"
              >
                <MailOutlined /> support@cirra.ai
              </a>
              <a
                href="https://docs.cirra.ai"
                target="_blank"
                rel="noopener noreferrer"
                className="walkthrough-support-link"
              >
                <FileTextOutlined /> Documentation
              </a>
            </div>
          </div>
        </div>
      );
    }

    const stepItems = steps.map((step, index) => ({
      title: step.shortTitle || step.title,
      status:
        index < state.currentStepIndex
          ? ('finish' as const)
          : index === state.currentStepIndex
            ? ('process' as const)
            : ('wait' as const),
    }));

    return (
      <div className="walkthrough-overlay" data-testid="walkthrough-overlay">
        <div className="walkthrough-header">
          <div className="walkthrough-logo">
            <Logo />
          </div>
          <div className="walkthrough-progress">
            <Steps
              current={state.currentStepIndex}
              items={stepItems}
              size="small"
              responsive={false}
            />
          </div>
          {isSkipAvailable ? (
            <Button
              type="text"
              className="walkthrough-close"
              icon={<CloseOutlined />}
              onClick={handleSkip}
              aria-label="Skip setup"
              disabled={navigationDisabled}
            />
          ) : (
            <div className="walkthrough-close-placeholder" />
          )}
        </div>

        <div className="walkthrough-body">
          <div className="walkthrough-content-card">
            <h2 className="walkthrough-step-title">{currentStep.title}</h2>
            <div className="walkthrough-step-content">
              {currentStep.content}
            </div>
          </div>
        </div>

        <div className="walkthrough-footer">
          <div className="walkthrough-support">
            <a
              href="mailto:support@cirra.ai"
              className="walkthrough-support-link"
            >
              <MailOutlined /> support@cirra.ai
            </a>
            <a
              href="https://docs.cirra.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="walkthrough-support-link"
            >
              <FileTextOutlined /> Documentation
            </a>
          </div>

          {isFirstStep && (
            <Checkbox
              checked={state.dontShowAgain}
              onChange={(e) =>
                setState((prev) => ({
                  ...prev,
                  dontShowAgain: e.target.checked,
                }))
              }
            >
              Don&apos;t show this walkthrough again
            </Checkbox>
          )}

          <div className="walkthrough-nav">
            {isSkipAvailable && (
              <Button
                onClick={handleSkip}
                type="text"
                disabled={navigationDisabled}
              >
                {isFirstStep ? "I'll set up later" : "I'll finish this later"}
              </Button>
            )}

            {!isFirstStep && (
              <Button
                onClick={handlePrevious}
                icon={<LeftOutlined />}
                disabled={navigationDisabled}
              >
                Previous
              </Button>
            )}

            <Button
              type="primary"
              onClick={handleNext}
              icon={!isLastStep ? <RightOutlined /> : undefined}
              disabled={navigationDisabled || !isCurrentStepComplete}
            >
              {isLastStep ? 'Get Started!' : 'Next'}
            </Button>
          </div>
        </div>
      </div>
    );
  }
);

Walkthrough.displayName = 'Walkthrough';

export default Walkthrough;
